import { create } from "zustand";
import { persist } from "zustand/middleware";

// A product can carry several independent variant groups at once
// (Color AND Wattage AND Length, etc.) — the cart line has to be keyed
// on the *combination* actually selected, not a single variant.
function lineKey(productId, options) {
  if (!options || options.length === 0) return `${productId}`;
  const sorted = [...options].sort((a, b) => a.name.localeCompare(b.name));
  return `${productId}::${sorted.map((o) => `${o.name}:${o.value}`).join("|")}`;
}

/**
 * Client-side cart (localStorage). Lives entirely in the browser until
 * checkout, where its contents are turned into a WhatsApp message or an
 * admin order-request — see features/checkout. `costPrice` here is
 * always 0 (the public product API never returns it — business-internal
 * data) and is purely a display-shape placeholder; the real cost
 * snapshot is looked up server-side from the authoritative Product row
 * when the order is created (see orderRequestsController.js), not
 * trusted from anything the client submits.
 */
const useCartStore = create(
  persist(
    (set, get) => ({
      items: [], // { key, productId, slug, name, price, costPrice, image, options, quantity }

      addItem: (product, selectedOptions = [], quantity = 1) => {
        const key = lineKey(product.id, selectedOptions);
        const items = get().items;
        const existing = items.find((i) => i.key === key);
        const priceDelta = selectedOptions.reduce((sum, o) => sum + (o.priceDelta || 0), 0);

        if (existing) {
          set({
            items: items.map((i) =>
              i.key === key ? { ...i, quantity: i.quantity + quantity } : i
            ),
          });
        } else {
          set({
            items: [
              ...items,
              {
                key,
                productId: product.id,
                slug: product.slug,
                name: product.name,
                price: product.price + priceDelta,
                costPrice: product.costPrice ?? 0,
                image: product.images?.[0]?.url,
                options: selectedOptions.map((o) => ({ name: o.name, value: o.value })),
                quantity,
              },
            ],
          });
        }
      },

      removeItem: (key) => set({ items: get().items.filter((i) => i.key !== key) }),

      updateQuantity: (key, quantity) =>
        set({
          items:
            quantity <= 0
              ? get().items.filter((i) => i.key !== key)
              : get().items.map((i) => (i.key === key ? { ...i, quantity } : i)),
        }),

      clear: () => set({ items: [] }),
    }),
    { name: "sc-cart" }
  )
);

export default useCartStore;

export function cartCount(items) {
  return items.reduce((sum, i) => sum + i.quantity, 0);
}

export function cartSubtotal(items) {
  return items.reduce((sum, i) => sum + i.price * i.quantity, 0);
}
